import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaFileAlt,
  FaDownload,
  FaTrash,
  FaImage,
  FaUser,
  FaTags,
  FaListAlt,
  FaCalendarAlt,
  FaCommentDots,
  FaEdit,
  FaCheck,
  FaTimes,
  FaCreditCard,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaReceipt,
  FaSave,
  FaClipboardList,
  FaCog,
} from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import {
  deleteReviewAnswer,
  downloadPrivacyFile,
  editReviewPaidStatus,
  editReviewStatus,
  getFileDownload,
  getResizeFileDownload,
  getReviewDetail,
  reviewRequestEdit,
} from "JS/API";
import { markTypeList, paidStatusList, paidTypeList } from "JS/array";
import { catchError, changeState, commonModalSetting } from "JS/common";
import FloatingList from "Components/FloatingList";
import RequestReviewFloating from "Components/RequestReviewFloating";
import { detailPageStyles } from "utils/designSystem";

const ReviewDetail = () => {
  // State declarations
  const [info, setInfo] = useState(null);
  const [alert, setAlert] = useState("");
  const [paidStatus, setPaidStatus] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [enterFloatSideBar, setEnterFloatSideBar] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [originalImageUrl, setOriginalImageUrl] = useState("");
  const [floatSideBar, setFloatSideBar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [edit, setEdit] = useState({
    progress: false,
    markName: false,
    markType: false,
    applicationNumber: false,
  });
  
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  
  const [byte, setByte] = useState(0);

  const { id } = useParams();
  const navigate = useNavigate();

  // 상태 배지 색상 매핑
  const statusColors = {
    request: { bg: '#3b82f6', color: '#ffffff' },
    canceled: { bg: '#dc2626', color: '#ffffff' },
    processing: { bg: '#f59e0b', color: '#ffffff' },
    completed: { bg: '#059669', color: '#ffffff' },
    rejected: { bg: '#991b1b', color: '#ffffff' }
  };

  const statusLabels = {
    request: '신청 완료',
    canceled: '취소',
    processing: '처리중',
    completed: '완료',
    rejected: '거절'
  };

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
      // PC에서는 댓글 사이드바 자동으로 열기
      if (window.innerWidth > 1024) {
        setFloatSideBar(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 초기 로드시 PC에서 댓글 사이드바 자동 열기
  useEffect(() => {
    if (!isMobile) {
      setFloatSideBar(true);
    }
  }, [isMobile]);

  // 스타일 정의
  const styles = {
    container: {
      padding: '24px',
      paddingRight: isMobile ? '24px' : (floatSideBar ? '374px' : '24px'),
      backgroundColor: '#f1f5f9',
      minHeight: '100vh',
      transition: 'padding-right 0.3s ease'
    },
    header: detailPageStyles.header,
    title: detailPageStyles.title,
    idBadge: detailPageStyles.idBadge,
    section: detailPageStyles.section,
    sectionHeader: {
      padding: '16px 24px',
      borderBottom: '1px solid #e2e8f0',
      backgroundColor: '#f8fafc',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    sectionTitle: detailPageStyles.sectionTitle,
    sectionContent: detailPageStyles.sectionContent,
    tableWrapper: detailPageStyles.tableWrapper,
    table: detailPageStyles.table,
    th: detailPageStyles.th,
    td: detailPageStyles.td,
    tdTitle: detailPageStyles.tdTitle,
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease'
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.2s ease',
      marginRight: '8px'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280',
      color: '#ffffff'
    },
    buttonDanger: {
      backgroundColor: '#dc2626',
      color: '#ffffff'
    },
    buttonSuccess: {
      backgroundColor: '#059669',
      color: '#ffffff'
    },
    statusBadge: {
      padding: '8px 16px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      minWidth: '100px',
      textAlign: 'center'
    },
    imageContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '24px'
    },
    imagePreview: {
      maxWidth: '400px',
      width: '100%',
      height: 'auto',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    productTag: {
      display: 'inline-block',
      padding: '6px 12px',
      margin: '4px',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '12px',
      color: '#374151'
    },
    productTagNonRegistered: {
      backgroundColor: '#fef2f2',
      borderColor: '#fecaca',
      color: '#dc2626'
    },
    textarea: {
      width: '100%',
      height: '300px',
      padding: '16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: '#f8fafc',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    paymentInfo: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '16px',
      padding: '20px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    paymentItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    paymentLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase'
    },
    paymentValue: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1e293b'
    },
    // 플로팅 버튼들
    floatingCommentButton: {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      border: 'none',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      cursor: 'pointer',
      zIndex: 1000,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: (isMobile && !floatSideBar) ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: (isMobile && !floatSideBar) ? 1 : 0,
      transform: (isMobile && !floatSideBar) ? 'scale(1) translateY(0)' : 'scale(0) translateY(20px)',
      pointerEvents: (isMobile && !floatSideBar) ? 'auto' : 'none'
    },
    floatingActionButton: {
      position: 'fixed',
      bottom: isMobile ? '100px' : '24px',
      right: isMobile ? '24px' : '400px',
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      border: 'none',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      cursor: 'pointer',
      zIndex: 9999,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    descriptionBox: {
      width: '100%',
      backgroundColor: '#f8fafc',
      padding: '20px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.6'
    },
    reviewSection: {
      position: 'relative'
    },
    reviewFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '12px',
      padding: '12px 0',
      borderTop: '1px solid #e2e8f0'
    },
    byteCounter: {
      fontSize: '12px',
      color: '#6b7280'
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    }
  };

  // API 호출 및 데이터 관리
  const getSelfApplicationInfo = async () => {
    const result = await getReviewDetail(id);
    if (typeof result === "object") {
      setInfo(result?.data);
      setReviewStatus(result?.data?.review_status);
      setPaidStatus(result?.data?.review_order_payment?.paid_status);
      setReviewComment(result?.data?.review_comment);
    } else {
      return await catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  useEffect(() => {
    getSelfApplicationInfo();
  }, [id]);

  useEffect(() => {
    if (reviewComment) {
      const byteLength = new Blob([reviewComment]).size;
      setByte(byteLength);
    } else {
      setByte(0);
    }
  }, [reviewComment]);

  // 이미지 관련 함수들
  useEffect(() => {
    if (info?.image_filename && info?.image_original_filename) {
      (async () => {
        const base64Url = await fetchUploadedImage(
          info.image_filename,
          info.image_original_filename
        );
        if (base64Url) setOriginalImageUrl(base64Url);
      })();
    }

    if (info?.mark_type === "figure" || info?.mark_type === "text") {
      if (info?.image_filename && info?.image_original_filename) {
        (async () => {
          const base64Url = await fetchUploadedImage(
            info.image_filename,
            info.image_original_filename
          );
          if (base64Url) setImageUrl(base64Url);
        })();
      }
    } else if (info?.mark_type === "compound") {
      if (info?.compound_image_filename) {
        (async () => {
          const base64Url = await fetchUploadedImage(
            info.compound_image_filename,
            "compound_image"
          );
          if (base64Url) setImageUrl(base64Url);
        })();
      }
    }
  }, [info]);

  const fetchUploadedImage = async (file_name, original_file_name) => {
    try {
      const response = await getResizeFileDownload(file_name, original_file_name);
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const mimeType = response.headers["content-type"];
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error("이미지 불러오기 실패:", error);
      return null;
    }
  };

  // 상태 변경 함수들
  const paidStatusEditFn = async () => {
    try {
      const data = {
        template_order_pk: info?.review_order_payment?.id,
        review_order_status: paidStatus,
      };

      const response = await editReviewPaidStatus(data);

      if (response?.status === 200) {
        commonModalSetting(setAlertBox, true, "alert", "변경되었습니다.");
        getSelfApplicationInfo();
      } else {
        commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
        getSelfApplicationInfo();
      }
    } catch (error) {
      console.error("결제 상태 수정 중 오류 발생:", error);
      commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
      getSelfApplicationInfo();
    }
  };

  const reviewStatusEditFn = async () => {
    try {
      const data = {
        template_pk: id,
        review_status: reviewStatus,
      };

      const response = await editReviewStatus(data);

      if (response?.status === 200) {
        commonModalSetting(setAlertBox, true, "alert", "변경되었습니다.");
        getSelfApplicationInfo();
      } else {
        commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
        getSelfApplicationInfo();
      }
    } catch (error) {
      console.error("검토 상태 수정 중 오류 발생:", error);
      commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
      getSelfApplicationInfo();
    }
  };

  // 다운로드 함수들
  const handleDownloadPrivacyFile = async (fileName, originalFileName) => {
    try {
      const blob = await downloadPrivacyFile(fileName, originalFileName);
      if (!(blob instanceof Blob)) {
        throw new Error("Response is not a Blob");
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading privacy file:", error);
    }
  };

  const handleOriginalImgDownload = async () => {
    try {
      const res = await getFileDownload(info.image_filename, info.image_original_filename);
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = info.image_original_filename || "downloaded_image";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("원본 이미지 다운로드 실패:", error);
    }
  };

  const handleImgDownload = () => {
    if (!originalImageUrl || !info?.image_original_filename) {
      alert("다운로드할 이미지가 없습니다.");
      return;
    }

    const link = document.createElement("a");
    link.download = info.image_original_filename;
    link.href = originalImageUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCompoundImgDownload = () => {
    if (!imageUrl || !info?.compound_image_filename) {
      alert("다운로드할 이미지가 없습니다.");
      return;
    }

    const link = document.createElement("a");
    link.download = 'compound';
    link.href = imageUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 검토 관련 함수들
  const reviewRequestEditFn = async (mode) => {
    if (mode !== "delete" && (!reviewComment || reviewComment.trim() === "")) {
      commonModalSetting(setAlertBox, true, "alert", "검토 결과를 입력해주세요.");
      return;
    }

    let review_comment;
    if (mode === "delete") review_comment = "";
    else review_comment = reviewComment;

    const data = {
      template_pk: info?.id,
      review_comment: review_comment,
    };

    const result = await reviewRequestEdit(info?.id, data);
    if (typeof result === "object") {
      if (mode === "apply") {
        setAlert("completeEdit");
        commonModalSetting(setAlertBox, true, "alert", "처리 되었습니다.");
        changeState(setEdit, "reviewStatus", true);
        getSelfApplicationInfo();
      } else if (mode === "edit") setEdit(false);
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const deleteReviewAnswerFn = async () => {
    try {
      const response = await deleteReviewAnswer(id);

      if (response?.status === 200) {
        setAlert("deleteComplete");
        commonModalSetting(setAlertBox, true, "alert", "삭제되었습니다.");
        setReviewComment("");
        getSelfApplicationInfo();
      } else {
        commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
        getSelfApplicationInfo();
      }
    } catch (error) {
      console.error("삭제 중 오류 발생:", error);
      commonModalSetting(setAlertBox, true, "alert", "다시 시도해주세요.");
      getSelfApplicationInfo();
    }
  };

  // 렌더링 함수들
  const renderApplicants = () => {
    return info?.applicants?.map((applicant, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{idx + 1}</td>
        <td style={styles.td}>{applicant.applicant_name}</td>
        <td style={styles.td}>{applicant.applicant_code}</td>
        <td style={styles.td}>{applicant.applicant_share} / 100</td>
      </tr>
    ));
  };

  const renderMarks = () => {
    if (!info?.marks || info.marks.length === 0) {
      return (
        <tr>
          <td colSpan="2" style={{...styles.td, textAlign: "center", color: '#6b7280'}}>
            표시할 상표명이 없습니다.
          </td>
        </tr>
      );
    }

    return info.marks.map((mark, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{mark.mark_step}</td>
        <td style={styles.td}>{mark.mark_name}</td>
      </tr>
    ));
  };

  const renderDetails = () => {
    return info?.detail_items?.map((item, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{item.mark_class}류</td>
        <td style={styles.td}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#059669' }}>
              고시 상품 ({Object.values(item.notice_register_product).flat().length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(item.notice_register_product).map(
                ([key, valueArray], index) =>
                  valueArray.map((e, subIndex) => (
                    <div
                      key={`${index}-${subIndex}`}
                      style={{
                        ...styles.productTag,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>{e}</span>
                      <span style={{ fontSize: '10px', fontWeight: '600', color: '#6b7280' }}>
                        {key}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </div>
          
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#dc2626' }}>
              비고시 상품 ({item.none_register_product.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {item.none_register_product.map((e, index) => (
                <div
                  key={index}
                  style={{...styles.productTag, ...styles.productTagNonRegistered}}
                >
                  {e}
                </div>
              ))}
            </div>
          </div>
        </td>
      </tr>
    ));
  };

  const renderImage = () => {
    if (!info) return null;
    const { mark_type } = info;
    
    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaImage />
            상표 이미지
          </h5>
        </div>
        <div style={styles.imageContainer}>
          {imageUrl && (
            <img
              src={imageUrl}
              alt="상표 이미지"
              style={styles.imagePreview}
            />
          )}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {mark_type !== "compound" && (
              <button
                style={styles.button}
                onClick={() => handleImgDownload()}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaDownload />
                이미지 다운로드
              </button>
            )}
            {mark_type === "compound" && (
              <button
                style={styles.button}
                onClick={() => handleCompoundImgDownload()}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                <FaDownload />
                복합 이미지 다운로드
              </button>
            )}
            <button
              style={{...styles.button, ...styles.buttonSecondary}}
              onClick={() => handleOriginalImgDownload()}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
            >
              <FaDownload />
              원본 이미지 다운로드
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 메인 컴포넌트 렌더링
  if (!info) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '16px', color: '#6b7280' }}>로딩 중...</div>
        </div>
      </div>
    );
  }

  const price = info?.review_order_payment?.merchant_price?.toLocaleString();
  const currentStatus = statusColors[info?.review_status] || statusColors.request;

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaClipboardList />
          REQUEST-REVIEW-DETAIL
          <span style={styles.idBadge}>ID: {id}</span>
        </h1>
      </div>

      {/* 셀프 출원 내역 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaFileAlt />
            셀프 출원 내역
          </h5>
          <div
            style={{
              ...styles.statusBadge,
              backgroundColor: currentStatus.bg,
              color: currentStatus.color
            }}
          >
            {statusLabels[info?.review_status] || '-'}
          </div>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <tbody>
                <tr>
                  <td style={{...styles.td, ...styles.tdTitle}}>템플릿명</td>
                  <td style={styles.td}>{info?.template_name || "-"}</td>
                  <td style={{...styles.td, ...styles.tdTitle}}>사용자 이름</td>
                  <td style={styles.td}>{info?.member_info?.user_name || "-"}</td>
                </tr>
                <tr>
                  <td style={{...styles.td, ...styles.tdTitle}}>상표 유형</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, backgroundColor: '#dbeafe', color: '#1d4ed8' }}>
                      {markTypeList[info?.mark_type?.toUpperCase()] || info?.mark_type}
                    </span>
                  </td>
                  <td style={{...styles.td, ...styles.tdTitle}}>등록일자</td>
                  <td style={styles.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaCalendarAlt size={12} />
                      {info?.created_at?.split(" ")[0] || "-"}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{...styles.td, ...styles.tdTitle}}>이메일</td>
                  <td style={styles.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaEnvelope size={12} />
                      {info?.member_info?.email || "-"}
                    </span>
                  </td>
                  <td style={{...styles.td, ...styles.tdTitle}}>휴대폰 번호</td>
                  <td style={styles.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaPhone size={12} />
                      {info?.member_info?.phone || "-"}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={{...styles.td, ...styles.tdTitle}}>결제상태 변경</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        style={styles.select}
                        value={paidStatus}
                        onChange={(e) => setPaidStatus(e.target.value)}
                      >
                        {Object.keys(paidStatusList).map((key) => (
                          <option key={key} value={key}>{paidStatusList[key]}</option>
                        ))}
                      </select>
                      <button
                        style={{...styles.button, padding: '8px 12px'}}
                        onClick={paidStatusEditFn}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        <FaCheck />
                        처리
                      </button>
                    </div>
                  </td>
                  <td style={{...styles.td, ...styles.tdTitle}}>검토 상태 변경</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        style={styles.select}
                        value={reviewStatus}
                        onChange={(e) => setReviewStatus(e.target.value)}
                      >
                        <option value={"request"}>신청 완료</option>
                        <option value={"canceled"}>취소</option>
                        <option value={"processing"}>검토중</option>
                        <option value={"completed"}>검토 완료</option>
                        <option value={"rejected"}>거절</option>
                      </select>
                      <button
                        style={{...styles.button, padding: '8px 12px'}}
                        onClick={reviewStatusEditFn}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        <FaCheck />
                        처리
                      </button>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{...styles.td, ...styles.tdTitle}}>결제 금액</td>
                  <td style={styles.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', color: '#059669' }}>
                      <FaCreditCard size={12} />
                      {price} 원
                    </span>
                  </td>
                  <td style={styles.td} colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상표 이미지 */}
      {renderImage()}

      {/* 출원인 정보 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaUser />
            출원인 정보
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>No.</th>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>특허고객번호</th>
                  <th style={styles.th}>지분</th>
                </tr>
              </thead>
              <tbody>{renderApplicants()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상표명 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaTags />
            상표명
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>단계</th>
                  <th style={styles.th}>상표명</th>
                </tr>
              </thead>
              <tbody>{renderMarks()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상표 설명 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaFileAlt />
            상표 설명
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.descriptionBox}>
            {info?.mark_description || "상표 설명이 없습니다."}
          </div>
        </div>
      </div>

      {/* 상품/업종 정보 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaListAlt />
            상품/업종 정보
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>상품 분류</th>
                  <th style={styles.th}>등록 상품</th>
                </tr>
              </thead>
              <tbody>{renderDetails()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 결제 정보 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaReceipt />
            결제 정보
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.paymentInfo}>
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}>결제 수단</span>
              <span style={styles.paymentValue}>
                {paidTypeList[info?.review_order_payment?.paid_method] || "-"}
              </span>
            </div>
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}>결제 일자</span>
              <span style={styles.paymentValue}>
                {info?.review_order_payment?.paid_at || "-"}
              </span>
            </div>
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}>처리 상태</span>
              <span style={styles.paymentValue}>
                {paidStatusList[info?.review_order_payment?.paid_status] || "-"}
              </span>
            </div>
            <div style={styles.paymentItem}>
              <span style={styles.paymentLabel}>세금계산서</span>
              <span style={styles.paymentValue}>
                {info?.review_order_payment?.tax_invoice_type !== "none" ? "발행" : "미발행"} / 
                {info?.review_order_payment?.paid_name || "---"}
              </span>
            </div>
          </div>

          {/* 세금계산서 상세 정보 */}
          {info?.review_order_payment?.tax_invoice_type === "private" && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151' }}>개인 발행</div>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaPhone size={12} />
                핸드폰 번호: {info?.review_order_payment?.phone || "-"}
              </span>
            </div>
          )}

          {info?.review_order_payment?.tax_invoice_type === "business" && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontWeight: '600', marginBottom: '12px', color: '#374151' }}>사업자 발행</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaReceipt size={12} />
                  사업자등록번호: {info?.review_order_payment?.biz_regist_number || "-"}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaBuilding size={12} />
                  회사명: {info?.review_order_payment?.company || "-"}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaUser size={12} />
                  소유주명: {info?.review_order_payment?.owner || "-"}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FaEnvelope size={12} />
                  이메일: {info?.review_order_payment?.email || "-"}
                </span>
                {info?.review_order_payment?.biz_license_original_filename && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <span>사업자 등록증: {info?.review_order_payment?.biz_license_original_filename}</span>
                    <button
                      style={{...styles.button, padding: '6px 12px', fontSize: '12px'}}
                      onClick={() =>
                        handleDownloadPrivacyFile(
                          info?.review_order_payment?.biz_license_filename,
                          info?.review_order_payment?.biz_license_original_filename
                        )
                      }
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      <FaDownload />
                      다운로드
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ 
            marginTop: '16px', 
            paddingTop: '16px', 
            borderTop: '2px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: '600',
            fontSize: '16px'
          }}>
            <span>총 상품 금액(1건)</span>
            <span style={{ color: '#059669' }}>
              {info?.review_order_payment?.merchant_price?.toLocaleString() || "-"}원
            </span>
          </div>
        </div>
      </div>

      {/* 출원 검토 결과 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaCommentDots />
            출원 검토 결과
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.reviewSection}>
            {info?.review_comment ? (
              // 검토 결과가 있을 때
              <div>
                {edit ? (
                  <textarea
                    style={styles.textarea}
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="검토 결과를 입력해주세요."
                  />
                ) : (
                  <div
                    style={{
                      ...styles.textarea,
                      padding: '16px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.6'
                    }}
                  >
                    {reviewComment}
                  </div>
                )}

                <div style={styles.reviewFooter}>
                  <div style={styles.byteCounter}>
                    <span style={{ fontWeight: '500' }}>{byte}</span>/3000bytes
                  </div>
                  <div style={styles.actionButtons}>
                    {edit ? (
                      <>
                        <button
                          style={{...styles.button, ...styles.buttonSuccess}}
                          onClick={() => reviewRequestEditFn("edit")}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                        >
                          <FaSave />
                          완료
                        </button>
                        <button
                          style={{...styles.button, ...styles.buttonSecondary}}
                          onClick={() => {
                            setEdit(false);
                            setReviewComment(info?.review_comment || "");
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                        >
                          <FaTimes />
                          취소
                        </button>
                      </>
                    ) : (
                      <button
                        style={styles.button}
                        onClick={() => setEdit(true)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        <FaEdit />
                        검토 수정
                      </button>
                    )}
                    <button
                      style={{...styles.button, ...styles.buttonDanger}}
                      onClick={() => {
                        setAlert("delete");
                        commonModalSetting(
                          setAlertBox,
                          true,
                          "confirm",
                          "검토 결과를 삭제하시면 다시 복구할 수 없습니다.<br/>검토 결과를 삭제하시겠습니까?"
                        );
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                    >
                      <FaTrash />
                      검토 삭제
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // 검토 결과가 없을 때
              <div>
                <textarea
                  style={styles.textarea}
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="검토 결과를 입력해주세요."
                />
                <div style={styles.reviewFooter}>
                  <div style={styles.byteCounter}>
                    <span style={{ fontWeight: '500' }}>{byte}</span>/3000bytes
                  </div>
                  <button
                    style={{...styles.button, ...styles.buttonSuccess}}
                    onClick={() => reviewRequestEditFn("apply")}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                  >
                    <FaSave />
                    완료
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 플로팅 버튼들 */}
      {/* 댓글 사이드바 토글 버튼 (모바일에서만) */}
      <button
        style={styles.floatingCommentButton}
        onClick={() => setFloatSideBar(true)}
        onMouseEnter={(e) => {
          if (isMobile && !floatSideBar) {
            e.target.style.transform = 'scale(1.15) translateY(-5px)';
            e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(0, 0, 0, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (isMobile && !floatSideBar) {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)';
          }
        }}
      >
        <FaCommentDots 
          size={24} 
          color="#ffffff"
          style={{
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
          }}
        />
      </button>

      {/* 추가 기능 플로팅 버튼 */}
      <button
        style={styles.floatingActionButton}
        onClick={() => setEnterFloatSideBar(true)}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1) translateY(-2px)';
          e.target.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.6), 0 8px 16px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1) translateY(0)';
          e.target.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
      >
        <FaCog 
          size={20} 
          color="#ffffff"
          style={{
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
          }}
        />
      </button>

      {/* 모달들 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
            else if (alert === "delete") {
              deleteReviewAnswerFn();
            }
          }}
        />
      )}

      {enterFloatSideBar && (
        <RequestReviewFloating
          setEnterFloatSideBar={setEnterFloatSideBar}
        />
      )}

      {/* 댓글 사이드바 - PC에서는 항상 표시, 모바일에서는 조건부 */}
      <FloatingList
        type={"REVIEW"}
        id={id}
        isOpen={floatSideBar}
        onClose={() => {
          if (isMobile) {
            setFloatSideBar(false);
          }
        }}
      />

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 6px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 3px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
        }

        .floating-comment-button:hover {
          transform: scale(1.15) translateY(-5px) !important;
        }

        .floating-action-button:hover {
          transform: scale(1.1) translateY(-2px) !important;
        }

        .review-section textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .payment-info {
          border: 1px solid #e2e8f0;
        }

        .status-badge {
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .section:hover {
          box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.06);
        }

        .button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
        }

        .product-tag:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .image-preview:hover {
          transform: scale(1.02);
          transition: transform 0.2s ease;
        }

        .table tbody tr:hover {
          background-color: #f8fafc;
        }

        @media (max-width: 1024px) {
          .action-buttons {
            flex-direction: column;
            gap: 4px;
          }
          
          .action-buttons button {
            width: 100%;
            justify-content: center;
          }
          
          .payment-info {
            grid-template-columns: 1fr;
          }
          
          .review-footer {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }
          
          .review-footer .action-buttons {
            justify-content: center;
          }
        }

        /* PC에서 댓글 사이드바가 열려있을 때 메인 컨텐츠 여백 조정 */
        @media (min-width: 1025px) {
          .container {
            transition: padding-right 0.3s ease;
          }
        }

        /* 모바일에서 플로팅 버튼 위치 조정 */
        @media (max-width: 1024px) {
          .floating-action-button {
            bottom: 100px;
          }
          
          .floating-comment-button {
            bottom: 24px;
          }
        }

        /* 애니메이션 효과 */
        .section {
          animation: fadeIn 0.5s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* 스크롤바 스타일링 */
        .container::-webkit-scrollbar {
          width: 8px;
        }

        .container::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        .container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .container::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default ReviewDetail;